import { PublicReader } from "./screens/PublicReader";
import { flushSync } from "react-dom";
import { storyTransition } from "./lib/storyTransition";
import { FeedView } from "./screens/Feed";
import { ToastNotice } from "./components/ToastNotice";
import { AnimatePresence, motion } from "framer-motion";
import { Check } from "lucide-react";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { AuthModal } from "./components/AuthModal";
import { ConfirmDialog, type ConfirmRequest } from "./components/ConfirmDialog";
import { GenerationDock } from "./components/GenerationDock";
import { GenerationOverlay } from "./components/GenerationOverlay";
import { Header } from "./components/Header";
import { BrandMark } from "./components/BrandMark";
import { StoryVersionChoice } from "./components/StoryVersionChoice";
import { DEFAULT_STORY_SETTINGS } from "./data/voices";
import { isJobRunning } from "./lib/job-progress";
import { api, type ApiStatus } from "./lib/api";
import { formatCredits } from "./lib/format";
import { notifyInBackground, requestStoryNotificationPermission } from "./lib/notifications";
import { libraryStoryPath, pathForView, publicStoryPath, routeFromUrl, worldPath } from "./lib/routes";
import { attachReadingProgress } from "./lib/useReadingProgress";
import type {
  AdminOverview, AdminReport, AdminUser, FeedData, GenerationJob, LedgerEntry, LibraryStory, PriceForecast,
  PricingConfig, PublicStory, ReactionValue, StorySettings, UniverseData, UniverseStoryMatch,
  UserProfile, View, WorldPage, WorldSummary,
} from "./types";
import { BRAND_DESCRIPTION, BRAND_NAME, BRAND_TAGLINE } from "./brand";

const AdminView = lazy(() => import("./screens/Admin").then((module) => ({ default: module.AdminView })));

const LibraryView = lazy(() => import("./screens/Library").then((module) => ({ default: module.LibraryView })));

const Reader = lazy(() => import("./screens/Reader").then((module) => ({ default: module.Reader })));
const Studio = lazy(() => import("./screens/Studio").then((module) => ({ default: module.Studio })));
const UniverseView = lazy(() => import("./screens/Universe").then((module) => ({ default: module.UniverseView })));
const WalletView = lazy(() => import("./screens/Wallet").then((module) => ({ default: module.WalletView })));


const OVERLAY_SEEN_KEY = "because-fm-overlay-seen-v1";
const SETTINGS_KEY = "because-fm-settings-v1";

function setDocumentMeta(selector: string, attributes: Record<string, string>, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

export default function App() {
  const [view, setView] = useState<View>(() => routeFromUrl(window.location.pathname, window.location.search).view); const [status, setStatus] = useState<ApiStatus | null>(null); const [user, setUser] = useState<UserProfile | null>(null);
  const [settings, setSettings] = useState<StorySettings>(() => { try { return { ...DEFAULT_STORY_SETTINGS, ...(JSON.parse(localStorage.getItem(SETTINGS_KEY) || "null") || {}) }; } catch { return DEFAULT_STORY_SETTINGS; } });
  const [forecast, setForecast] = useState<PriceForecast | null>(null);
  const [forecastError, setForecastError] = useState(false);
  const [forecastAttempt, setForecastAttempt] = useState(0);
  const [stories, setStories] = useState<LibraryStory[]>([]); const [jobs, setJobs] = useState<GenerationJob[]>([]);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]); const [ledgerNextCursor, setLedgerNextCursor] = useState<string | null>(null); const [ledgerLoading, setLedgerLoading] = useState(false); const [universe, setUniverse] = useState<UniverseData | null>(null); const [worldPage, setWorldPage] = useState<WorldPage | null>(null); const [worldLoading, setWorldLoading] = useState(false); const [admin, setAdmin] = useState<AdminOverview | null>(null); const [accountLoading, setAccountLoading] = useState(false);
  const [feed, setFeed] = useState<FeedData | null>(null); const [feedLoading, setFeedLoading] = useState(false); const [feedError, setFeedError] = useState(""); const [currentPublic, setCurrentPublic] = useState<PublicStory | null>(null);
  const [currentStory, setCurrentStory] = useState<LibraryStory | null>(null); const [activeJob, setActiveJob] = useState<GenerationJob | null>(null); const [startTopic, setStartTopic] = useState(() => { try { return sessionStorage.getItem("because-fm-draft") || ""; } catch { return ""; } });
  const [readerReturnView, setReaderReturnView] = useState<"studio" | "library" | "universe">("library"); const [publicReturnView, setPublicReturnView] = useState<"studio" | "feed" | "universe">("feed");
  const [studioVersionChoice, setStudioVersionChoice] = useState<{ topic: string; match: UniverseStoryMatch } | null>(null);
  const [authOpen, setAuthOpen] = useState(false); const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest | null>(null); const [toast, setToast] = useState(""); const eventSourceRef = useRef<EventSource | null>(null); const terminalJobsRef = useRef(new Set<string>()); const jobsPrimedRef = useRef(false); const feedRequestRef = useRef(0);
  useEffect(() => { try { sessionStorage.setItem("because-fm-draft", startTopic); } catch { /* The draft remains in memory when browser storage is unavailable. */ } }, [startTopic]);

  const feedFilters = useRef({ query: "", category: "", sort: "new" });
  const feedPosition = useRef(0);
  const feedFocus = useRef<HTMLElement | null>(null);
  const routeRequest = useRef(0);
  const publicRequest = useRef(0);
  const appliedFeedFilters = useRef("");
  const [openingId, setOpeningId] = useState("");
  const [feedMounted, setFeedMounted] = useState(view === "feed");
  useEffect(() => { if (view === "feed") setFeedMounted(true); }, [view]);
  function returnToFeed(update: () => void, position = feedPosition.current) {
    const storyId = document.querySelector<HTMLElement>("[data-public-story]")?.dataset.publicStory;
    storyTransition(update, { storyId, afterCommit: () => {
      window.scrollTo({ top: position, behavior: "instant" });
      if (feedFocus.current?.isConnected) feedFocus.current.focus({ preventScroll: true });
    } });
  }
  function scrollToStoryTarget(position?: number) {
    if (position !== undefined) { window.scrollTo({ top: position, behavior: "instant" }); return; }
    let id = "";
    try { id = decodeURIComponent(window.location.hash.slice(1)); } catch {}
    if (id) document.getElementById(id)?.scrollIntoView({ block: "start", behavior: "instant" });
    else window.scrollTo({ top: 0, behavior: "instant" });
  }

  function navigate(next: View | (() => void), explicitPath?: string) {
    const apply = typeof next === "function" ? next : () => setView(next);
    if (typeof next !== "function" && next === view && (!explicitPath || `${window.location.pathname}${window.location.search}` === explicitPath)) return;
    routeRequest.current++;
    const targetPath = explicitPath || (typeof next === "string" ? pathForView(next) : undefined);
    if (targetPath && `${window.location.pathname}${window.location.search}` !== targetPath) {
      window.history.replaceState({ ...window.history.state, scrollY: window.scrollY }, "", window.location.href);
      window.history.pushState({ becauseFm: true, canGoBack: true }, "", targetPath);
    }
    if (next === "feed" && document.querySelector(".public-reader-page")) return returnToFeed(apply);
    apply();
    window.scrollTo({ top: 0, behavior: "instant" });
  }

  function navigateBack(fallback: View) {
    const historyState = window.history.state as { becauseFm?: boolean; canGoBack?: boolean } | null;
    if (historyState?.becauseFm && historyState.canGoBack) window.history.back();
    else navigate(fallback);
  }

  useEffect(() => { api<ApiStatus>("/api/status").then((data) => { setStatus(data); setUser(data.user); }).catch(() => setStatus({ live: false, mode: "demo", user: null })); }, []);
  useEffect(() => {
    const isPublicStory = view === "public-reader" && currentPublic;
    const title = isPublicStory ? `${currentPublic.title} — ${BRAND_NAME}` : `${BRAND_NAME} — ${BRAND_TAGLINE.toLowerCase()}`;
    const description = isPublicStory ? currentPublic.excerpt : BRAND_DESCRIPTION;
    const canonicalUrl = `${window.location.origin}${isPublicStory ? publicStoryPath(currentPublic.slug) : window.location.pathname}`;
    document.title = title;
    setDocumentMeta('meta[name="description"]', { name: "description" }, description);
    setDocumentMeta('meta[property="og:title"]', { property: "og:title" }, title);
    setDocumentMeta('meta[property="og:description"]', { property: "og:description" }, description);
    setDocumentMeta('meta[property="og:type"]', { property: "og:type" }, isPublicStory ? "article" : "website");
    setDocumentMeta('meta[property="og:url"]', { property: "og:url" }, canonicalUrl);
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = canonicalUrl;
  }, [view, currentPublic]);
  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    const syncLocation = async (event?: PopStateEvent) => {
      const request = ++routeRequest.current;
      let origin = window.location.href;
      const valid = () => request === routeRequest.current && window.location.href === origin;
      const route = routeFromUrl(window.location.pathname, window.location.search);
      if (route.view === "public-reader") {
        if (route.legacy) window.history.replaceState({ becauseFm: true, canGoBack: false }, "", publicStoryPath(route.id));
        origin = window.location.href;
        try {
          const data = await api<{ story: PublicStory }>(`/api/feed/${encodeURIComponent(route.id)}`);
          if (!valid()) return;
          const show = () => { setCurrentPublic(data.story); setView("public-reader"); };
          storyTransition(show, { storyId: data.story.id, afterCommit: () => scrollToStoryTarget(event && Number.isFinite(event.state?.scrollY) ? event.state.scrollY : undefined) });
          void recordPublicView(data.story.id);
        } catch (error) {
          if (!valid()) return;
          setToast((error as Error).message);
          setView("feed");
          window.history.replaceState({ becauseFm: true, canGoBack: false }, "", pathForView("feed"));
        }
      } else if (route.view === "reader") {
        setCurrentStory(null);
        setView("reader");
        try {
          const data = await api<{ story: LibraryStory }>(`/api/stories/${encodeURIComponent(route.id)}`);
          if (!valid()) return;
          setCurrentStory(data.story);
        } catch (error) {
          if (!valid()) return;
          setToast((error as Error).message);
          setView("library");
          window.history.replaceState({ becauseFm: true, canGoBack: false }, "", pathForView("library"));
        }
      } else if (route.view === "universe") {
        if (event) {
          flushSync(() => setView("universe"));
          window.scrollTo({ top: Number(window.history.state?.scrollY) || 0, behavior: "instant" });
        } else setView("universe");
        if (route.world) void loadWorld(route.world);
        else setWorldPage(null);
      } else if (route.view === "feed" && document.querySelector(".public-reader-page")) {
        returnToFeed(() => setView("feed"), Number.isFinite(event?.state?.scrollY) ? event!.state.scrollY : feedPosition.current);
      } else if (event) {
        flushSync(() => setView(route.view));
        window.scrollTo({ top: Number(window.history.state?.scrollY) || 0, behavior: "instant" });
      } else setView(route.view);
    };
    const existing = window.history.state as { becauseFm?: boolean; canGoBack?: boolean } | null;
    window.history.replaceState({ ...existing, becauseFm: true, canGoBack: Boolean(existing?.becauseFm && existing.canGoBack) }, "", window.location.href);
    void syncLocation();
    window.addEventListener("popstate", syncLocation);
    return () => { window.removeEventListener("popstate", syncLocation); window.history.scrollRestoration = previousScrollRestoration; };
  }, []);
  useEffect(() => {
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); } catch { /* Settings still work without storage. */ }
    const controller = new AbortController();
    let alive = true;
    setForecast(null);
    setForecastError(false);
    const timeout = setTimeout(() => controller.abort(), 12000);
    const timer = setTimeout(() => {
      api<{ forecast: PriceForecast }>("/api/forecast", { method: "POST", signal: controller.signal, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ settings }) })
        .then((data) => { if (alive) setForecast(data.forecast); })
        .catch(() => { if (alive) setForecastError(true); })
        .finally(() => clearTimeout(timeout));
    }, 180);
    return () => { alive = false; clearTimeout(timer); clearTimeout(timeout); controller.abort(); };
  }, [settings, user?.balanceCredits, forecastAttempt]);
  useEffect(() => { if (toast) { const timer = setTimeout(() => setToast(""), 3200); return () => clearTimeout(timer); } }, [toast]);

  async function refreshAccount(nextUser = user) {
    if (!nextUser) { setStories([]); setJobs([]); setLedger([]); setLedgerNextCursor(null); setAccountLoading(false); return; }
    setAccountLoading(true);
    try {
      const [storiesData, jobsData, ledgerData] = await Promise.all([api<{ stories: LibraryStory[] }>("/api/stories"), api<{ jobs: GenerationJob[] }>("/api/jobs"), api<{ entries: LedgerEntry[]; nextCursor: string | null; user: UserProfile }>("/api/ledger?limit=20")]);
      setStories(storiesData.stories); setJobs(jobsData.jobs); setLedger(ledgerData.entries); setLedgerNextCursor(ledgerData.nextCursor); setUser(ledgerData.user);
    } finally {
      setAccountLoading(false);
    }
  }

  useEffect(() => { if (user) void refreshAccount(user); }, [user?.id]);
  // Место остановки живёт вместе с облачной библиотекой: вошли — подтянули
  // прочитанное с других устройств и дослали то, что накопилось на этом.
  useEffect(() => { void attachReadingProgress(user?.id || null); }, [user?.id]);
  useEffect(() => {
    if (!user) return;
    const syncJobs = async () => {
      try {
        const data = await api<{ jobs: GenerationJob[] }>("/api/jobs");
        setJobs(data.jobs);
        const newlyFinished = data.jobs.filter((job) => ["completed", "failed", "cancelled"].includes(job.status) && !terminalJobsRef.current.has(job.id));
        newlyFinished.forEach((job) => terminalJobsRef.current.add(job.id));
        if (jobsPrimedRef.current) {
          newlyFinished.forEach((job) => {
            if (job.status === "completed") notifyInBackground("История готова", `«${job.topic}» сохранена в библиотеке.`, job.id);
          });
        }
        jobsPrimedRef.current = true;
        if (newlyFinished.length) await refreshAccount();
      } catch { /* the next interval will retry */ }
    };
    const timer = setInterval(syncJobs, 5000);
    return () => clearInterval(timer);
  }, [user?.id]);
  const universeLoadedRef = useRef<{ userId: string; storyCount: number } | null>(null);
  useEffect(() => {
    // Атлас нужен и анонимам: миры открыты без входа, просто без счётчика «у вас».
    if (view !== "universe" && !universe) return;
    const target = { userId: user?.id || "", storyCount: stories.length };
    const loaded = universeLoadedRef.current;
    if (loaded && loaded.userId === target.userId && loaded.storyCount === target.storyCount) return;
    universeLoadedRef.current = target;
    api<UniverseData>("/api/universe")
      .then(setUniverse)
      // Провалилась загрузка — сбрасываем метку, чтобы следующая смена stories.length
      // повторила запрос, а не залипал старый результат.
      .catch(() => { if (universeLoadedRef.current === target) universeLoadedRef.current = null; });
  }, [view, user?.id, stories.length]);

  async function loadWorld(worldId: string, from = 0) {
    setWorldLoading(true);
    try {
      const page = await api<WorldPage>(`/api/universe/worlds/${encodeURIComponent(worldId)}?from=${from}`);
      setWorldPage(page);
      return page;
    } catch (error) {
      setToast((error as Error).message);
      setWorldPage(null);
      return null;
    } finally {
      setWorldLoading(false);
    }
  }
  function openWorld(world: WorldSummary) {
    navigate(() => { setWorldPage(null); void loadWorld(world.id); }, worldPath(world.id));
  }
  function closeWorld() {
    navigate(() => setWorldPage(null), pathForView("universe"));
  }
  useEffect(() => {
    const reload = () => {
      void api<UniverseData>("/api/universe").then(setUniverse).catch(() => { universeLoadedRef.current = null; });
      if (view === "universe" && worldPage) void loadWorld(worldPage.world.id, worldPage.topicsFrom);
    };
    window.addEventListener("topics-changed", reload);
    return () => window.removeEventListener("topics-changed", reload);
  }, [view, worldPage?.world.id, worldPage?.topicsFrom]);
  useEffect(() => {
    if (view !== "wallet" || !user) return;
    setLedgerLoading(true);
    api<{ entries: LedgerEntry[]; nextCursor: string | null; user: UserProfile }>("/api/ledger?limit=20")
      .then((data) => { setLedger(data.entries); setLedgerNextCursor(data.nextCursor); setUser(data.user); })
      .catch(() => undefined)
      .finally(() => setLedgerLoading(false));
  }, [view, user?.id]);
  useEffect(() => { if (view === "admin" && user?.role === "admin") api<AdminOverview>("/api/admin/overview").then(setAdmin).catch((error) => setToast(error.message)); }, [view, user?.role]);

  useEffect(() => { if (currentPublic && view === "public-reader") void openPublicStory(currentPublic.id, false); }, [user?.id]);

  useEffect(() => {
    eventSourceRef.current?.close();
    if (!activeJob || ["completed", "failed", "cancelled"].includes(activeJob.status)) return;
    const source = new EventSource(`/api/jobs/${activeJob.id}/events`); eventSourceRef.current = source;
    const receive = (event: MessageEvent) => { const data = JSON.parse(event.data) as { job: GenerationJob }; setActiveJob(data.job); setJobs((items) => [data.job, ...items.filter((item) => item.id !== data.job.id)]); };
    source.addEventListener("snapshot", receive as EventListener);
    source.addEventListener("done", ((event: MessageEvent) => { const data = JSON.parse(event.data) as { job: GenerationJob }; setActiveJob(data.job); source.close(); notifyInBackground("История готова", `«${data.job.topic}» сохранена в библиотеке.`, data.job.id); void (async () => { await refreshAccount(); if (data.job.storyId) { const result = await api<{ story: LibraryStory }>(`/api/stories/${data.job.storyId}`); setCurrentStory(result.story); setReaderReturnView("library"); setTimeout(() => { setActiveJob(null); navigate("reader", libraryStoryPath(result.story.id)); }, 700); } })(); }) as EventListener);
    source.addEventListener("failed", ((event: MessageEvent) => { const data = JSON.parse(event.data) as { job: GenerationJob }; setActiveJob(data.job); source.close(); notifyInBackground("История не сложилась", data.job.error || "Резерв возвращён — тему можно запустить снова.", data.job.id); setToast(data.job.error || "Историю не удалось закончить — резерв возвращён"); void refreshAccount(); }) as EventListener);
    source.onerror = () => { source.close(); setTimeout(() => api<{ job: GenerationJob }>(`/api/jobs/${activeJob.id}`).then((data) => setActiveJob(data.job)).catch(() => undefined), 1200); };
    return () => source.close();
  }, [activeJob?.id]);

  async function generate(topic: string) {
    if (!user) { setAuthOpen(true); setToast("Войдите, чтобы сохранить готовую историю"); return; }
    try {
      const data = await api<{ job: GenerationJob; user: UserProfile }>("/api/jobs", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ topic, settings }) });
      setUser(data.user); setJobs((items) => [data.job, ...items]);
      // Полноэкранную редакцию показываем один раз за браузер: посмотреть на неё
      // интересно, но держать её перед глазами каждую генерацию незачем — дальше
      // хватает пульта внизу экрана.
      if (!localStorage.getItem(OVERLAY_SEEN_KEY)) { localStorage.setItem(OVERLAY_SEEN_KEY, "1"); setActiveJob(data.job); }
      else setToast("Пишем в фоне — ход выпуска виден на пульте внизу");
      requestStoryNotificationPermission();
    } catch (error) { if ((error as { status?: number }).status === 402) navigate("wallet"); setToast((error as Error).message); }
  }
  async function generateFromStudio(topic: string) {
    try {
      const data = await api<{ match: UniverseStoryMatch }>("/api/story-match", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ topic }) });
      if (data.match.library || data.match.broadcast) { setStudioVersionChoice({ topic, match: data.match }); return; }
    } catch { /* a failed lookup must not block writing */ }
    await generate(topic);
  }

  async function authSuccess(nextUser: UserProfile, first: boolean) { setUser(nextUser); setAuthOpen(false); setToast(first ? "Вы — первый владелец. Админка уже открыта для вас." : `С возвращением, ${nextUser.displayName}`); await refreshAccount(nextUser); }
  async function logout() { await api("/api/auth/logout", { method: "POST" }); setUser(null); setStories([]); setJobs([]); setLedger([]); setLedgerNextCursor(null); setCurrentStory(null); setCurrentPublic(null); navigate("studio"); setToast("До следующей истории"); }
  async function toggleFavorite(story: LibraryStory) { const data = await api<{ story: LibraryStory }>(`/api/stories/${story.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ favorite: !story.favorite }) }); setStories((items) => items.map((item) => item.id === story.id ? data.story : item)); if (currentStory?.id === story.id) setCurrentStory(data.story); }
  async function deleteStory(id: string) {
    const story = stories.find((item) => item.id === id);
    setConfirmRequest({
      title: "Удалить историю?",
      message: `«${story?.title || "Без названия"}» исчезнет вместе с публикацией и иллюстрациями. Отменить удаление будет нельзя.`,
      confirmLabel: "Удалить навсегда",
      onConfirm: async () => {
        try {
          await api(`/api/stories/${id}`, { method: "DELETE" });
          setStories((items) => items.filter((item) => item.id !== id));
          setToast("История удалена");
        } catch (error) { setToast((error as Error).message); }
      },
    });
  }
  async function togglePublish(story: LibraryStory) {
    const data = await api<{ story: LibraryStory }>(`/api/stories/${story.id}/publish`, { method: story.published ? "DELETE" : "POST" });
    setStories((items) => items.map((item) => item.id === story.id ? data.story : item)); if (currentStory?.id === story.id) setCurrentStory(data.story);
    setToast(story.published ? "История снята с публикации" : "История опубликована");
    void loadFeed(feedFilters.current);
  }
  async function loadFeed(filters: { query: string; category: string; sort: string } = { query: "", category: "", sort: "new" }, cursor = "") {
    const filterKey = JSON.stringify(filters);
    if (cursor && filterKey !== appliedFeedFilters.current) return;
    const requestId = ++feedRequestRef.current;
    feedFilters.current = filters;
    setFeedLoading(true); setFeedError(""); const params = new URLSearchParams(); if (filters.query) params.set("query", filters.query); if (filters.category) params.set("category", filters.category); params.set("sort", filters.sort); if (cursor) params.set("cursor", cursor);
    try {
      const next = await api<FeedData>(`/api/feed?${params}`);
      if (requestId !== feedRequestRef.current) return;
      appliedFeedFilters.current = filterKey;
      setFeed((current) => cursor && current ? { ...next, stories: [...new Map([...current.stories, ...next.stories].map(story => [story.id, story])).values()] } : next);
    } catch (error) { if (requestId === feedRequestRef.current) setFeedError((error as Error).message); }
    finally { if (requestId === feedRequestRef.current) setFeedLoading(false); }
  }
  function showLibraryStory(story: LibraryStory, returnView: "studio" | "library" | "universe" = "library") {
    setReaderReturnView(returnView); navigate(() => { setCurrentStory(story); setView("reader"); }, libraryStoryPath(story.id));
  }
  async function openLibraryStory(id: string, returnView: "studio" | "library" | "universe" = "library") {
    try { const data = await api<{ story: LibraryStory }>(`/api/stories/${encodeURIComponent(id)}`); showLibraryStory(data.story, returnView); }
    catch (error) { setToast((error as Error).message); }
  }
  async function recordPublicView(id: string) {
    try {
      const data = await api<Pick<PublicStory, "viewCount">>(`/api/feed/${encodeURIComponent(id)}/view`, { method: "POST" });
      setCurrentPublic((story) => story?.id === id ? { ...story, viewCount: data.viewCount } : story);
      setFeed((value) => value ? { ...value, stories: value.stories.map((story) => story.id === id ? { ...story, viewCount: data.viewCount } : story) } : value);
      setStories((items) => items.map((story) => story.publicationId === id ? { ...story, viewCount: data.viewCount } : story));
      setCurrentStory((story) => story?.publicationId === id ? { ...story, viewCount: data.viewCount } : story);
    } catch {}
  }
  async function openPublicStory(id: string, shouldNavigate = true, returnView: "studio" | "feed" | "universe" = "feed", chapter?: string) {
    const request = ++publicRequest.current;
    const routeVersion = routeRequest.current;
    const originPath = window.location.href;
    const valid = () => request === publicRequest.current && routeVersion === routeRequest.current && window.location.href === originPath;
    if (shouldNavigate) {
      setOpeningId(id);
      if (returnView === "feed") feedFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }
    try {
      const data = await api<{ story: PublicStory }>(`/api/feed/${encodeURIComponent(id)}`);
      if (!valid()) return;
      const reveal = () => {
        setCurrentPublic(data.story);
        if (shouldNavigate) { setPublicReturnView(returnView); navigate("public-reader", publicStoryPath(data.story.slug) + (chapter ? "#" + encodeURIComponent(chapter) : "")); }
      };
      if (shouldNavigate && returnView === "feed" && document.querySelector(".feed-page")?.getClientRects().length) {
        feedPosition.current = window.scrollY;
        storyTransition(reveal, { storyId: data.story.id, afterCommit: () => scrollToStoryTarget() });
      } else reveal();
      void recordPublicView(data.story.id);
    }
    catch (error) { if (valid()) setToast((error as Error).message); }
    finally { if (request === publicRequest.current) setOpeningId(""); }
  }
  async function reactPublic(value: ReactionValue) {
    if (!currentPublic) return;
    const data = await api<Pick<PublicStory, "likes" | "dislikes" | "score" | "viewerReaction">>(`/api/feed/${currentPublic.id}/reaction`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ value }) });
    setCurrentPublic((story) => story?.id === currentPublic.id ? { ...story, ...data } : story); setFeed((value) => value ? { ...value, stories: value.stories.map((story) => story.id === currentPublic.id ? { ...story, ...data } : story) } : value);
  }
  async function commentPublic(body: string) {
    if (!currentPublic) return; const data = await api<{ comment: NonNullable<PublicStory["comments"]>[number] }>(`/api/feed/${currentPublic.id}/comments`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ body }) });
    setCurrentPublic((story) => story?.id === currentPublic.id ? { ...story, comments: [...(story.comments || []), data.comment], commentCount: story.commentCount + 1 } : story); setFeed((value) => value ? { ...value, stories: value.stories.map((story) => story.id === currentPublic.id ? { ...story, commentCount: story.commentCount + 1 } : story) } : value);
  }
  async function deletePublicComment(id: string) {
    if (!currentPublic) return;
    const publicationId = currentPublic.id;
    setConfirmRequest({
      title: "Удалить заметку?",
      message: "Заметка исчезнет из разговора насовсем — восстановить её не получится.",
      confirmLabel: "Удалить заметку",
      onConfirm: async () => {
        try {
          await api(`/api/feed/${publicationId}/comments/${id}`, { method: "DELETE" });
          setCurrentPublic((story) => story?.id === publicationId ? { ...story, comments: (story.comments || []).filter((item) => item.id !== id), commentCount: Math.max(0, story.commentCount - 1) } : story);
          setFeed(value => value ? { ...value, stories: value.stories.map(story => story.id === publicationId ? { ...story, commentCount: Math.max(0, story.commentCount - 1) } : story) } : value); setToast("Заметка удалена");
        } catch (error) { setToast((error as Error).message); }
      },
    });
  }
  async function reportPublic(reason: string) {
    if (!currentPublic) return;
    try {
      const data = await api<{ duplicate?: boolean }>(`/api/feed/${currentPublic.id}/report`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason }) });
      setToast(data.duplicate ? "Вы уже отправляли жалобу на эту историю" : "Жалоба отправлена");
    } catch (error) { setToast((error as Error).message); }
  }
  async function reportPublicComment(id: string, reason: string) {
    if (!currentPublic) return;
    try {
      const data = await api<{ duplicate?: boolean }>(`/api/feed/${currentPublic.id}/comments/${id}/report`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason }) });
      setToast(data.duplicate ? "Вы уже жаловались на этот комментарий" : "Жалоба отправлена");
    } catch (error) { setToast((error as Error).message); }
  }
  async function resolveReport(report: AdminReport) {
    try {
      const data = await api<{ reports: AdminReport[] }>(`/api/admin/reports/${report.id}/resolve`, { method: "POST" });
      setAdmin((value) => value ? { ...value, reports: data.reports } : value); setToast("Жалоба помечена разобранной");
    } catch (error) { setToast((error as Error).message); }
  }
  async function hideReportedPublication(report: AdminReport) {
    if (!report.publicationId) return;
    setConfirmRequest({
      title: "Снять историю с эфира?",
      message: `«${report.storyTitle || "Без названия"}» будет снята с публикации, но останется в библиотеке автора.`,
      confirmLabel: "Снять с эфира",
      onConfirm: async () => {
        try {
          const data = await api<{ reports: AdminReport[] }>(`/api/admin/publications/${report.publicationId}/hide`, { method: "POST" });
          setAdmin((value) => value ? { ...value, reports: data.reports } : value); setFeed(null); setToast("История снята с эфира");
        } catch (error) { setToast((error as Error).message); }
      },
    });
  }
  async function deleteReportedComment(report: AdminReport) {
    if (!report.commentId || !report.publicationId) return;
    setConfirmRequest({
      title: "Удалить заметку?",
      message: "Заметка исчезнет из разговора насовсем, а жалоба закроется автоматически.",
      confirmLabel: "Удалить заметку",
      onConfirm: async () => {
        try {
          await api(`/api/feed/${report.publicationId}/comments/${report.commentId}`, { method: "DELETE" });
          const overview = await api<AdminOverview>("/api/admin/overview");
          setAdmin(overview); setToast("Заметка удалена, жалоба закрыта");
        } catch (error) { setToast((error as Error).message); }
      },
    });
  }
  function pickFromUniverse(topic: string) { navigate(() => { setStartTopic(topic); setView("studio"); }, pathForView("studio")); }
  async function purchase() { if (!user) return setAuthOpen(true); const data = await api<{ message: string }>("/api/purchase", { method: "POST" }); setToast(data.message); }
  async function loadMoreLedger() {
    if (!ledgerNextCursor || ledgerLoading) return;
    setLedgerLoading(true);
    try {
      const data = await api<{ entries: LedgerEntry[]; nextCursor: string | null; user: UserProfile }>(`/api/ledger?limit=20&cursor=${encodeURIComponent(ledgerNextCursor)}`);
      setLedger((items) => [...items, ...data.entries.filter((entry) => !items.some((current) => current.id === entry.id))]);
      setLedgerNextCursor(data.nextCursor); setUser(data.user);
    } catch (error) { setToast((error as Error).message); }
    finally { setLedgerLoading(false); }
  }
  async function savePricing(pricing: PricingConfig) { const data = await api<{ pricing: PricingConfig }>("/api/admin/pricing", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(pricing) }); setAdmin((value) => value ? { ...value, pricing: data.pricing } : value); setToast("Тарифы сохранены. Новые прогнозы уже считают по ним."); }
  async function grant(target: AdminUser, amount: number, note: string) { await api(`/api/admin/users/${target.id}/credits`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ amountCredits: amount, note }) }); const overview = await api<AdminOverview>("/api/admin/overview"); setAdmin(overview); if (target.id === user?.id) await refreshAccount(); setToast(`${formatCredits(amount)} кредитов начислено для ${target.displayName}`); }

  return <div className="app-shell"><Header view={view} setView={navigate} status={status} user={user} onAuth={() => setAuthOpen(true)} onLogout={logout} />
    <Suspense fallback={<main className="route-loading" role="status">Загружаем…</main>}>
    {view === "studio" && <Studio onGenerate={generateFromStudio} settings={settings} setSettings={setSettings} forecast={forecast} forecastError={forecastError} onRetryForecast={() => setForecastAttempt(value => value + 1)} startTopic={startTopic} onTopicChange={setStartTopic} onWallet={() => navigate("wallet")} onKnownTopic={(topic, match) => setStudioVersionChoice({ topic: topic.title, match })} />}
    {view === "reader" && currentStory && <Reader key={currentStory.id} story={currentStory} onBack={() => navigateBack(readerReturnView)} onFavorite={() => toggleFavorite(currentStory)} onPublish={() => togglePublish(currentStory)} onToast={setToast} />}
    {view === "library" && <LibraryView user={user} stories={stories} loading={accountLoading} onAuth={() => setAuthOpen(true)} onOpen={(story) => showLibraryStory(story)} onToggleFavorite={toggleFavorite} onPublish={togglePublish} onDelete={deleteStory} />}
    {(feedMounted || view === "feed") && <div hidden={view !== "feed"}><FeedView data={feed} loading={feedLoading} error={feedError} user={user} openingId={openingId} onLoad={loadFeed} onOpen={(story, chapter) => openPublicStory(story.id, true, "feed", chapter)} onAuth={() => setAuthOpen(true)} onCreate={() => navigate("studio")} /></div>}
    {view === "public-reader" && currentPublic && <PublicReader key={currentPublic.id} story={currentPublic} user={user} onBack={() => navigateBack(publicReturnView)} backLabel={publicReturnView === "universe" ? "Вернуться к темам" : publicReturnView === "studio" ? "Вернуться в студию" : "К историям"} onAuth={() => setAuthOpen(true)} onReact={reactPublic} onComment={commentPublic} onDeleteComment={deletePublicComment} onReport={reportPublic} onReportComment={reportPublicComment} onToast={setToast} />}
    {view === "universe" && <UniverseView data={universe} worldPage={worldPage} worldLoading={worldLoading} user={user} onOpenWorld={openWorld} onCloseWorld={closeWorld} onRotateTopics={(worldId, from) => void loadWorld(worldId, from)} onPick={pickFromUniverse} onOpenStory={(story) => story.source === "library" ? void openLibraryStory(story.id, "universe") : void openPublicStory(story.id, true, "universe")} onAuth={() => setAuthOpen(true)} />}
    {view === "wallet" && <WalletView user={user} entries={ledger} nextCursor={ledgerNextCursor} loading={accountLoading} loadingMore={ledgerLoading} onLoadMore={loadMoreLedger} onAuth={() => setAuthOpen(true)} onPurchase={purchase} />}
    {view === "admin" && user?.role === "admin" && <AdminView overview={admin} onSavePricing={savePricing} onGrant={grant} onResolveReport={resolveReport} onHidePublication={hideReportedPublication} onDeleteReportedComment={deleteReportedComment} onToast={setToast} onOpenJob={setActiveJob} />}
    </Suspense>
    <AnimatePresence>{studioVersionChoice && <StoryVersionChoice
      topic={{ title: studioVersionChoice.topic }}
      match={studioVersionChoice.match}
      onClose={() => setStudioVersionChoice(null)}
      onRead={(story) => { setStudioVersionChoice(null); if (story.source === "library") void openLibraryStory(story.id, "studio"); else void openPublicStory(story.id, true, "studio"); }}
      onWrite={() => {
        const topic = studioVersionChoice.topic;
        setStudioVersionChoice(null);
        setStartTopic(topic);
        if (view === "studio") window.scrollTo({ top: 0, behavior: "smooth" });
        else navigate("studio");
      }}
    />}</AnimatePresence>
    <footer className="site-footer"><BrandMark /><p>{BRAND_DESCRIPTION}</p><a href="mailto:hello@because.fm">hello@because.fm</a></footer>
    <AnimatePresence>{activeJob && isJobRunning(activeJob) && <GenerationOverlay job={activeJob} onHide={() => { setActiveJob(null); setToast("История продолжает готовиться в фоне"); }} />}</AnimatePresence>
    <GenerationDock jobs={activeJob && isJobRunning(activeJob) ? [] : jobs.filter(isJobRunning)} onOpen={setActiveJob} />
    <AnimatePresence>{authOpen && <AuthModal onClose={() => setAuthOpen(false)} onSuccess={authSuccess} />}</AnimatePresence>
    <AnimatePresence>{confirmRequest && <ConfirmDialog request={confirmRequest} onClose={() => setConfirmRequest(null)} />}</AnimatePresence>
    <ToastNotice message={toast} />
  </div>;
}
